<template>
    <Cell
        class="BulkImportCorrectionTableCell"
        :class="{'BulkImportCorrectionTableCell--focus': focused}"
        v-bind="plainAttrs"
    >
        <JsonSchemaFormElement
            :id="id"
            :form="formProperty.form"
            :required="formProperty.required"
            :model-value="formValue"
            :custom-component-input="getCustomComponent"
            :deletable="formProperty.form.type === 'object' && !formProperty.required"
            touched
            v-bind="listenerAttrs"
            @focus="onFocus"
            @blur="onBlur"
        >
            <template #icon>
                <FontAwesomeIcon :icon="faEdit" />
            </template>
        </JsonSchemaFormElement>
    </Cell>
</template>

<script lang="ts">
    import { Component, Prop, Vue } from '@evidentid/vue-property-decorator';

    import { faEdit } from '@fortawesome/free-solid-svg-icons';
    import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

    import { JsonSchemaFormElement } from '@evidentid/dashboard-commons/components/JsonSchemaForm';
    import JsonSchemaAddressForm
        from '@evidentid/dashboard-commons/components/JsonSchemaForm/customElements/JsonSchemaAddressForm.vue';
    import JsonSchemaCountryEnumForm
        from '@evidentid/dashboard-commons/components/JsonSchemaForm/customElements/JsonSchemaCountryEnumForm.vue';
    import JsonSchemaConstForm
        from '@evidentid/dashboard-commons/components/JsonSchemaForm/elements/JsonSchemaConstForm.vue';
    import { Cell } from '@evidentid/dashboard-commons/components/Table';
    import { JsonFormProperty, JsonFormType } from '@evidentid/json-schema/interfaces/JsonForm';
    import {
        TprmSchemaDisplayFormat,
    } from '@evidentid/tprm-portal-lib/models/common/TprmSchemaDisplayFormat.model';
    import { EntityInput } from '@evidentid/tprm-portal-lib/models/dashboard';
    import { omitListenerAttrs, pickListenerAttrs } from '@evidentid/vue-commons/utils/attrsListeners';

    import {
        BulkImportArrayField, BulkImportBooleanField, BulkImportEnumField,
    } from '@/modules/entity-management/components/BulkImportCustomForms';
    import BulkImportObjectField
        from '@/modules/entity-management/components/BulkImportCustomForms/BulkImportObjectField.vue';
    import BulkImportStringField
        from '@/modules/entity-management/components/BulkImportCustomForms/BulkImportStringField.vue';
    import { getJsonSchemaCompFunction } from '@/utils/getJsonSchemaCustomCompFuntion';

    // inheritAttrs is off because the caller's `@input` has to reach the form element and
    // nothing else. Left on, Vue 3 would also bind it to this cell's root as a native
    // listener, and the `input` event bubbling out of the field would call the caller a
    // second time with the raw DOM Event -- which lands in the entity as the field's new
    // value and blanks the input as you type.
    @Component({
        components: { Cell, JsonSchemaFormElement, FontAwesomeIcon },
        inheritAttrs: false,
    })
    export default class BulkImportCorrectionTableCell extends Vue {
        @Prop({ type: String })
        private id!: string;

        @Prop({ type: String, default: null })
        private entityPropExtension!: 'insuredFields' | 'exceptions' | null;

        @Prop({ type: Object, default: () => ({}) })
        private formProperty!: JsonFormProperty;

        @Prop({ type: Object, default: () => ({}) })
        private value!: EntityInput;

        private faEdit = faEdit;
        private focused: boolean = false;

        private customComponents = {
            [JsonFormType.boolean]: BulkImportBooleanField,
            [JsonFormType.array]: BulkImportArrayField,
            [JsonFormType.string]: BulkImportStringField,
            [JsonFormType.object]: BulkImportObjectField,
            [JsonFormType.enum]: BulkImportEnumField,
            [TprmSchemaDisplayFormat.hidden]: JsonSchemaConstForm,
            [TprmSchemaDisplayFormat.address]: JsonSchemaAddressForm,
            [TprmSchemaDisplayFormat.country]: JsonSchemaCountryEnumForm,
        };
        private getCustomComponent = getJsonSchemaCompFunction(this.customComponents);

        private get listenerAttrs(): Record<string, unknown> {
            return pickListenerAttrs(this.$attrs);
        }

        private get plainAttrs(): Record<string, unknown> {
            return omitListenerAttrs(this.$attrs);
        }

        private get formValue() {
            const customProperties = this.value.insuredFields || {};
            const exceptions = this.value.exceptions || {};
            if (this.entityPropExtension === 'insuredFields') {
                return customProperties[this.formProperty.name];
            } else if (this.entityPropExtension === 'exceptions') {
                return exceptions[this.formProperty.name];
            } else {
                return this.value[this.formProperty.name as keyof EntityInput];
            }
        }

        private onFocus() {
            this.focused = true;
        }

        private onBlur() {
            this.focused = false;
        }
    }
</script>
